// Non-component exports for SfxProviderSelector, split out of the .tsx so the
// component file only exports components (react-refresh/only-export-components).
// Consumed by SfxDurationSlider + SfxGenerationPanel.
//
// PRESENTATION ONLY. Everything factual — duration limits, quality stars,
// pricing, T2A/V2A capability — comes from constants/soundEffects.ts, which
// mirrors the backend. This table used to restate those numbers and had drifted
// badly: AudioCraft was capped at 8s (backend allows 30s), HunyuanFoley at 10s
// (30s), LTX-2 at 6s (10s), and Stable Audio was shown as 1 star while the
// backend rates it 4.

import {
  SFX_PROVIDERS,
  getSfxPricingLabel,
  getSfxProviderLimits,
  getSfxQualityRating,
  type SfxProvider,
} from '../../constants/soundEffects';

export type { SfxProvider };

export type SfxProviderType = 'T2A' | 'V2A';

export interface SfxProviderInfo {
  value: SfxProvider;
  label: string;
  description: string;
  type: SfxProviderType;
  quality: number;
  pricing: string;
  maxDuration: number;
  icon: 'Wand2' | 'Video' | 'Music' | 'Sparkles';
  badge?: string;
}

interface SfxProviderPresentation {
  value: SfxProvider;
  label: string;
  description: string;
  icon: SfxProviderInfo['icon'];
  badge?: string;
}

const CLOUD_PROVIDER_PRESENTATION: SfxProviderPresentation[] = [
  {
    value: SFX_PROVIDERS.STABLE_AUDIO,
    label: 'Stable Audio',
    description: 'Basic ambient audio generation',
    icon: 'Music',
  },
  {
    value: SFX_PROVIDERS.LTX2_AUDIO,
    label: 'LTX-2 Audio',
    description: 'Fast lightweight audio generation',
    icon: 'Wand2',
  },
  {
    value: SFX_PROVIDERS.AUDIOCRAFT,
    label: 'AudioCraft',
    description: "Meta's AudioGen - high quality sound effects",
    icon: 'Sparkles',
    // recommended: true — set via PickerOption mapping in the selector
  },
  {
    value: SFX_PROVIDERS.HUNYUAN_FOLEY,
    label: 'HunyuanVideo Foley',
    description: 'Video-to-Audio synchronization',
    icon: 'Video',
  },
];

export const SFX_PROVIDER_OPTIONS: SfxProviderInfo[] = CLOUD_PROVIDER_PRESENTATION.map(
  (presentation) => {
    const limits = getSfxProviderLimits(presentation.value);
    return {
      ...presentation,
      type: limits.requiresVideo ? 'V2A' : 'T2A',
      quality: getSfxQualityRating(presentation.value),
      pricing: getSfxPricingLabel(presentation.value),
      maxDuration: limits.maxDuration,
    };
  }
);
